import { usePrompt } from '@ur-solutions/react-components'
import { ApproveCommentModal } from './ApproveCommentModal'
import type { useSalaryModuleConfiguration } from './hooks/useSalaryModuleConfiguration'
import type { useWorkPeriodMigrations } from './hooks/useWorkPeriodMigrations'
import type { ApproveWorkPeriodNode } from './types.graphql'

type ApprovalSide = 'driver' | 'manager'

type SalaryConfiguration = ReturnType<typeof useSalaryModuleConfiguration>
type PatchWorkPeriod = ReturnType<
  typeof useWorkPeriodMigrations
>['patchWorkPeriod']

type UseWorkPeriodApprovalActionsArgs = {
  initRefetch: () => void
  patchWorkPeriod: PatchWorkPeriod
  salaryConfiguration: SalaryConfiguration
  commentRequiredDriverApproval: string
  commentRequiredManagerApproval: string
}

export function useWorkPeriodApprovalActions({
  initRefetch,
  patchWorkPeriod,
  salaryConfiguration,
  commentRequiredDriverApproval,
  commentRequiredManagerApproval,
}: UseWorkPeriodApprovalActionsArgs) {
  const addPrompt = usePrompt()

  function getApprovalConfig(
    workPeriod: ApproveWorkPeriodNode,
    side: ApprovalSide
  ) {
    if (side === 'driver') {
      return {
        approved: workPeriod.approvedByUser,
        comment: workPeriod.userComment,
        approvedKey: 'approvedByUser' as const,
        commentKey: 'userComment' as const,
        commentRequiredOnApproval:
          salaryConfiguration.requireCommentOnDriverApproval,
        commentRequiredError: commentRequiredDriverApproval,
      }
    }

    return {
      approved: workPeriod.approvedByManager,
      comment: workPeriod.managerComment,
      approvedKey: 'approvedByManager' as const,
      commentKey: 'managerComment' as const,
      commentRequiredOnApproval:
        salaryConfiguration.requireCommentOnManagerApproval,
      commentRequiredError: commentRequiredManagerApproval,
    }
  }

  async function openApprovalCommentFlow(
    workPeriod: ApproveWorkPeriodNode,
    side: ApprovalSide
  ) {
    const config = getApprovalConfig(workPeriod, side)
    const { data } = await addPrompt<{
      comment: string
      approved: boolean
    } | null>(resolve => (
      <ApproveCommentModal
        existingComment={config.comment}
        existingApproved={config.approved}
        commentRequiredOnApproval={config.commentRequiredOnApproval}
        commentRequiredError={config.commentRequiredError}
        onSubmit={resolve}
      />
    ))
    if (!data) return

    await patchWorkPeriod({
      variables: {
        id: workPeriod.id,
        input: {
          [config.approvedKey]: data.approved,
          [config.commentKey]: data.comment,
        },
      },
    })
    initRefetch()
  }

  async function toggleApproval(
    workPeriod: ApproveWorkPeriodNode,
    side: ApprovalSide
  ) {
    const config = getApprovalConfig(workPeriod, side)
    if (config.commentRequiredOnApproval) {
      await openApprovalCommentFlow(workPeriod, side)
      return
    }

    await patchWorkPeriod({
      variables: {
        id: workPeriod.id,
        input: {
          [config.approvedKey]: !config.approved,
        },
      },
    })
    initRefetch()
  }

  return {
    openApprovalCommentFlow,
    toggleApproval,
  }
}
